import { useLayoutEffect, useRef, type ReactNode } from 'react';

export interface PopoverProps {
  /** The accessible name of the trigger button. */
  triggerLabel: string;
  /** What the trigger button shows. */
  trigger: ReactNode;
  triggerClassName?: string;
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'left' | 'right';
  /** The panel. */
  children: ReactNode;
}

// The attribute that tells <orno-popover> which child opens it; no JSX typing knows it.
const TRIGGER = { trigger: '' };

/**
 * `<orno-popover>` with a button that opens it. orno wires the button (aria-expanded, aria-controls, a click that
 * toggles), closes the panel on a click elsewhere or Escape and gives focus back to the button. The element moves
 * everything but the trigger into its panel, so the panel's content sits in one stable `<div>`. The panel is a plain
 * group, not a menu, so the `aria-haspopup="true"` (which means a menu) that orno 2.1.0 puts on the trigger is taken
 * off again (remove when orno leaves it off for an unnamed panel).
 */
export function Popover({
  triggerLabel,
  trigger,
  triggerClassName = 'btn-ghost',
  placement = 'bottom-start',
  children,
}: PopoverProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    triggerRef.current?.removeAttribute('aria-haspopup');
  }, []);

  return (
    <orno-popover placement={placement}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-label={triggerLabel}
        {...TRIGGER}
      >
        {trigger}
      </button>
      <div className="orno-content">{children}</div>
    </orno-popover>
  );
}
